import React, { useState, useCallback, useRef } from 'react';
import {
  TextField, Chip, Box, Button,
  Typography,
  Paper, Grid,
  Card, CardContent, CardHeader
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useActionSettings } from '../../hooks/useActionSettings';
import ActionSettingsLayout from '../ActionSettingsHeader';
import AddIcon from '@mui/icons-material/Add';
import TagsDropdown from '../TagsDropdown';
import TagEditor from '../Widgets/TagEditor';

const SettingCallMCP = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();
  
  const [mcpCommand, setMcpCommand] = useState('npx');
  const [mcpArgsString, setMcpArgsString] = useState('');
  const [toolName, setToolName] = useState('');
  const [toolParams, setToolParams] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState('');

  const commandRef = useRef(null);
  const argsRef = useRef(null);
  const toolNameRef = useRef(null);
  const paramsRef = useRef(null);

  // Use unified action settings hook
  const {
    isEnabled,
    setIsEnabled,
    isSaveDisabled,
    conditions,
    id,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleConditionsChange,
    handleSave: baseHandleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleCloseStatusWindow,
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'callmcp',
    onSaveStatusChange,
    customFields: {
      mcpCommand: 'npx',
      mcpArgs: [],
      toolName: '',
      toolParams: {},
      replaceExistingDocument: false
    },
    customComparison: (initial) => {
      let parsedToolParams = {};
      try {
        parsedToolParams = JSON.parse(toolParams || '{}');
      } catch (e) {}

      const currentMcpArgs = mcpArgsString.split('\n').map(arg => arg.trim()).filter(arg => arg);

      return (
        initial.mcpCommand === mcpCommand &&
        JSON.stringify(initial.mcpArgs || []) === JSON.stringify(currentMcpArgs) &&
        initial.toolName === toolName &&
        JSON.stringify(initial.toolParams || {}) === JSON.stringify(parsedToolParams)
      );
    },
    customSaveData: () => {
      let toolParamsToSend = {};
      try {
        toolParamsToSend = JSON.parse(toolParams || '{}');
      } catch (e) {
        console.error("Invalid JSON in toolParams:", e);
      }

      return {
        mcpCommand,
        mcpArgs: mcpArgsString.split('\n').map(arg => arg.trim()).filter(arg => arg),
        toolName,
        toolParams: toolParamsToSend,
        useConditions: true,
        conditions
      };
    },
    loadDataFunction: (data) => {
      console.log('Loading call MCP data:', data);
      
      let parsedData = {};
      if (data.data && typeof data.data === 'string') {
        try {
          parsedData = JSON.parse(data.data);
        } catch (e) {
          console.error('Failed to parse data JSON:', e);
          return;
        }
      } else if (data.customData) {
        parsedData = data.customData;
      }
      
      setMcpCommand(parsedData.mcpCommand || 'npx');
      setMcpArgsString((parsedData.mcpArgs || []).join('\n'));
      setToolName(parsedData.toolName || '');
      if (parsedData.toolParams) {
        setToolParams(JSON.stringify(parsedData.toolParams, null, 2));
      } else {
        setToolParams('');
      }
    },
    enableConditions: true
  });

  const handleMenuOpen = useCallback((event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  }, []);

  const handleMainTagMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleTagSelect = useCallback((tagValue) => {
    if (menuType === 'command') {
      commandRef.current?.insertTag(tagValue);
    } else if (menuType === 'args') {
      argsRef.current?.insertTag(tagValue);
    } else if (menuType === 'toolName') {
      toolNameRef.current?.insertTag(tagValue);
    } else if (menuType === 'params') {
      paramsRef.current?.insertTag(tagValue);
    }
    setAnchorEl(null);
  }, [menuType]);

  const handleSave = () => {
    if (toolParams.trim() !== '') {
      try {
        JSON.parse(toolParams);
      } catch (e) {
        alert(t("action_common.tool_parameters_invalid_json", "Invalid JSON in tool parameters"));
        return;
      }
    }
    baseHandleSave();
  };

  return (
    <ActionSettingsLayout
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      title={t('settingCallMCP.title', 'Call MCP Tool')}
      isSaveDisabled={isSaveDisabled}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
      enableConditions={true}
      popUpOpen={popUpOpen}
      forceRunResult={forceRunResult}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
    >
      <Grid item xs={12}>
        <Card sx={{
          mb: 2,
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          border: '1px solid #5757b010',
          overflow: 'hidden',
        }}>
          <CardHeader
            title={
              <Typography sx={{
                fontWeight: 700,
                color: '#444',
                fontSize: { xs: '1rem', sm: '1.25rem' },
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1
              }}>
                <Box component="span" sx={{
                  bgcolor: '#5757b0',
                  color: 'white',
                  borderRadius: '6px',
                  px: { xs: 1, sm: 1.5 },
                  py: { xs: 0.5, sm: 0.7 },
                  mr: { xs: 1, sm: 2 },
                  fontSize: { xs: '0.75rem', sm: '0.85rem' },
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                  boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
                }}>
                  MCP
                </Box>
                {t('settingCallMCP.mcp_config_title', 'Configuration')}
              </Typography>
            }
            sx={{
              borderBottom: '1px solid #f0f0f8',
              backgroundColor: '#fafbff',
              p: { xs: 1.5, sm: 2.5 },
            }}
          />
          <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                    {t('settingCallMCP.mcpCommand', 'Command (e.g. npx)')}
                  </Typography>
                  <TagEditor
                    ref={commandRef}
                    value={mcpCommand}
                    onChange={setMcpCommand}
                    placeholder="npx"
                    singleLine
                    onTagClick={(e) => handleMenuOpen(e, 'command')}
                    tags={tags}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                    {t('settingCallMCP.mcpArgs', 'Arguments (one per line)')}
                  </Typography>
                  <TagEditor
                    ref={argsRef}
                    value={mcpArgsString}
                    onChange={setMcpArgsString}
                    multiline
                    minRows={2}
                    placeholder="-y @playwright/mcp"
                    onTagClick={(e) => handleMenuOpen(e, 'args')}
                    tags={tags}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                    {t('settingCallMCP.toolName', 'Tool Name')}
                  </Typography>
                  <TagEditor
                    ref={toolNameRef}
                    value={toolName}
                    onChange={setToolName}
                    placeholder="screenshot"
                    singleLine
                    tags={tags}
                    onTagClick={(e) => handleMenuOpen(e, 'toolName')}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 600, color: '#444', mb: 0.5 }}>
                    {t('settingCallMCP.toolParams', 'Tool Parameters (JSON)')}
                  </Typography>
                  <TagEditor
                    ref={paramsRef}
                    value={toolParams}
                    onChange={setToolParams}
                    multiline
                    minRows={4}
                    placeholder='{ "url": "{url}" }'
                    tags={tags}
                    onTagClick={(e) => handleMenuOpen(e, 'params')}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    label='{ "url": "{url}" }'
                    size="small"
                    onClick={() => setToolParams('{ "url": "{url}" }')}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#efefff' } }}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <TagsDropdown
        tags={tags}
        handleTagSelect={handleTagSelect}
        anchorEl={anchorEl}
        setAnchorEl={handleMainTagMenuClose}
      />
    </ActionSettingsLayout>
  );
};

export default React.memo(SettingCallMCP);
